import { Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SaveIcon from '@mui/icons-material/Save'
import { useCallback, useEffect, useState } from 'react'
import { apiDelete, apiGet, apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { serializeTemplateWidgets } from './widgetTemplateUtils'
import { stripWidgetSettings } from './widgetTemplateParse'
import PreviewTemplateDialog from './PreviewTemplateDialog'

/**
 * Widget template manager, opened from the MOS widget edition toolbar. Lets a
 * MOS_EDIT user save the current layout as a template (HU-04), apply a template
 * to the current device overwriting its layout (HU-02, with a confirmation when
 * the current layout is not empty — HU-03) and delete templates (HU-01).
 */
const WidgetTemplateManagerDialog = ({ open, setOpen, deviceId, widgets, onApplied, onEditTemplate }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const [AlertElement, showAlert] = AlertUI()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [newName, setNewName] = useState('')
  const [copySettings, setCopySettings] = useState(true)
  const [busy, setBusy] = useState(false)
  // { type: 'apply' | 'delete', template } — drives the confirmation dialog.
  const [pendingAction, setPendingAction] = useState(null)
  const [previewTemplate, setPreviewTemplate] = useState(null)
  // Per-row overflow menu (preview / edit / delete).
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [menuTemplate, setMenuTemplate] = useState(null)

  const openRowMenu = (event, template) => {
    setMenuAnchorEl(event.currentTarget)
    setMenuTemplate(template)
  }
  const closeRowMenu = () => {
    setMenuAnchorEl(null)
    setMenuTemplate(null)
  }

  const hasLayout = Array.isArray(widgets) && widgets.length > 0

  const loadTemplates = useCallback(async () => {
    setLoading(true)
    try {
      const result = await apiGet('widgets/templates')
      setTemplates(Array.isArray(result) ? result : [])
    } catch (e) {
      showAlert({ title: t_('error loading widget templates'), message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [showAlert, t_])

  useEffect(() => {
    if (open) {
      setNewName('')
      setCopySettings(true)
      loadTemplates()
    }
  }, [open, loadTemplates])

  const handleSaveCurrent = async () => {
    // Templates are device layouts: creating one from the initial screen (no
    // device) would produce a template that cannot be applied to any device.
    if (!deviceId) return
    const name = newName.trim()
    if (!name) {
      showAlert({ title: t_('A name is required'), message: '', severity: 'error' })
      return
    }
    setBusy(true)
    try {
      await apiPost('widgets/templates', { name, widgets: serializeTemplateWidgets(copySettings ? widgets : stripWidgetSettings(widgets)) })
      showAlert({ title: t_('Template saved'), message: '', severity: 'success' })
      setNewName('')
      await loadTemplates()
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const doApply = async (template) => {
    setBusy(true)
    try {
      await apiPost('widgets/templates/' + template.id + '/apply/' + deviceId, {})
      showAlert({ title: t_('Template applied'), message: '', severity: 'success' })
      if (onApplied) onApplied()
      setOpen(false)
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const doDelete = async (template) => {
    setBusy(true)
    try {
      await apiDelete('widgets/templates/' + template.id)
      showAlert({ title: t_('Template deleted'), message: '', severity: 'success' })
      await loadTemplates()
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const requestApply = (template) => {
    if (!deviceId) return
    if (hasLayout) {
      setPendingAction({ type: 'apply', template })
    } else {
      doApply(template)
    }
  }

  const confirmPending = () => {
    const action = pendingAction
    setPendingAction(null)
    if (!action) return
    if (action.type === 'apply') {
      doApply(action.template)
    } else {
      doDelete(action.template)
    }
  }

  const confirmMessage = pendingAction?.type === 'apply'
    ? t_('Applying this template will overwrite the current layout of this device. Continue?')
    : t_('This template will be permanently deleted. Continue?')

  return (
    <>
      <SmartDialog
        setOpen={open}
        closeCallback={() => setOpen(false)}
        close
        accept={false}
        cancel={false}
        paperWidth='min(64rem, 92vw)'
        title={{ icon: null, render: <Typography variant='h4'>{t_('Widget templates')}</Typography> }}
        renderComponent={
          <Box sx={{ padding: '1rem', width: '100%', boxSizing: 'border-box' }}>
            {AlertElement}
            <Stack direction='row' spacing={2} alignItems='center'>
              <TextField
                label={t_('New template name')}
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                sx={{ flexGrow: 1 }}
                inputProps={{ maxLength: 255 }}
              />
              <Button
                variant='contained'
                startIcon={<SaveIcon />}
                onClick={handleSaveCurrent}
                disabled={busy || !deviceId || !hasLayout || newName.trim().length === 0}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {t_('Save current layout')}
              </Button>
            </Stack>
            <FormControlLabel
              control={<Checkbox checked={copySettings} onChange={(event) => setCopySettings(event.target.checked)} size='small' />}
              label={t_('Copy widget settings')}
            />
            {!deviceId
              ? (
                <Typography variant='caption' color='text.secondary'>
                  {t_('Select a device to save its layout as a template')}
                </Typography>
                )
              : !hasLayout
                  ? (
                    <Typography variant='caption' color='text.secondary'>
                      {t_('Add at least one widget to save the current layout as a template')}
                    </Typography>
                    )
                  : null}

            <Divider sx={{ my: 2 }} />

            {loading
              ? <Stack alignItems='center' sx={{ py: 3 }}><CircularProgress /></Stack>
              : templates.length === 0
                ? <Typography color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>{t_('No templates yet')}</Typography>
                : (
                  <Stack divider={<Divider flexItem />} spacing={0}>
                    {templates.map((template) => (
                      <Stack key={template.id} direction='row' spacing={1} alignItems='center' sx={{ py: 1 }}>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography noWrap>{template.name}</Typography>
                          {template.updatedAt && (
                            <Typography variant='caption' color='text.secondary'>{formatDateTime(template.updatedAt)}</Typography>
                          )}
                        </Box>
                        <Tooltip title={deviceId ? t_('Apply to this device') : t_('Select a device to apply a template')}>
                          <span>
                            <Button
                              size='small'
                              variant='outlined'
                              startIcon={<PlaylistAddCheckIcon />}
                              onClick={() => requestApply(template)}
                              disabled={busy || !deviceId}
                            >
                              {t_('Apply')}
                            </Button>
                          </span>
                        </Tooltip>
                        <IconButton size='small' onClick={(event) => openRowMenu(event, template)} disabled={busy} aria-label={t_('More options')}>
                          <MoreVertIcon />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                  )}
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeRowMenu}>
              <MenuItem onClick={() => { const template = menuTemplate; closeRowMenu(); setPreviewTemplate(template) }}>
                <ListItemIcon><VisibilityIcon fontSize='small' /></ListItemIcon>
                <ListItemText>{t_('Preview')}</ListItemText>
              </MenuItem>
              <MenuItem disabled={!onEditTemplate} onClick={() => { const template = menuTemplate; closeRowMenu(); if (onEditTemplate) onEditTemplate(template) }}>
                <ListItemIcon><EditIcon fontSize='small' /></ListItemIcon>
                <ListItemText>{t_('Edit')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { const template = menuTemplate; closeRowMenu(); setPendingAction({ type: 'delete', template }) }}>
                <ListItemIcon><DeleteOutlineIcon fontSize='small' color='error' /></ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>{t_('Delete')}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        }
      />

      <SmartDialog
        setOpen={Boolean(pendingAction)}
        title={createDialogTitle({ color: 'warning', title: t_('Attention'), icon: WarningAmberIcon })}
        message={confirmMessage}
        accept
        acceptLabel={t_('Continue')}
        acceptCallback={confirmPending}
        cancel
        cancelCallback={() => setPendingAction(null)}
      />

      <PreviewTemplateDialog
        open={Boolean(previewTemplate)}
        template={previewTemplate}
        device={deviceId ? { id: deviceId } : null}
        onClose={() => setPreviewTemplate(null)}
        onApply={(template) => { setPreviewTemplate(null); requestApply(template) }}
      />
    </>
  )
}

export default WidgetTemplateManagerDialog
